import * as Yup from 'yup'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import { environment } from '@mortgage-pos/ui/env'
import { CustomError } from '@mortgage-pos/error-handling'
import { autoCompleteAddressQuestions } from '@mortgage-pos/data'
import { Questions, Validations, ValidationRule } from '@mortgage-pos/types'
import { differenceInYears } from 'date-fns'

const phoneRegex =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

Yup.addMethod(Yup.string, 'phone', function phone(message: string) {
  return this.test('phone', message, (value) => phoneRegex.test(value))
})

export const PASSWORD_PATTERNS = {
  CHARACTER_LENGTH: '{8,}',
  UPPERCASE: '[A-Z]',
  LOWERCASE: '[a-z]',
  NUMBER: '[0-9]',
  SPECIAL_CHARACTER: '[!#$%&*+-/:;<=>?@_]',
  FULL_TEST: '[A-Za-z\\d!#$%&*+-/:;<=>?@_]',
}

const {
  CHARACTER_LENGTH,
  UPPERCASE,
  LOWERCASE,
  NUMBER,
  SPECIAL_CHARACTER,
  FULL_TEST,
} = PASSWORD_PATTERNS

const passwordRegex = new RegExp(
  `^(?=.*${UPPERCASE})(?=.*${LOWERCASE})(?=.*${NUMBER})(?=.*${SPECIAL_CHARACTER})${FULL_TEST}${CHARACTER_LENGTH}$`
)

Yup.addMethod(
  Yup.string,
  'passwordStrength',
  function passwordStrength(message: string) {
    return this.test('passwordStrength', message, (value) =>
      passwordRegex.test(value)
    )
  }
)

const ssnRegex = new RegExp(
  environment.appEnv !== 'prod'
    ? // Non production environments can use SSNs that start with '000' and '666'
      /^(?!111-11-1111|222-22-2222|333-33-3333|444-44-4444|555-55-5555|666-66-6666|777-77-7777|888-88-8888|999-99-9999|123-45-6789|219-09-9999|078-05-1120)(?!9\d{2})\d{3}[-―](?!00)\d{2}[-―](?!0{4})\d{4}$/
    : // Production environment regex
      /^(?!111-11-1111|222-22-2222|333-33-3333|444-44-4444|555-55-5555|666-66-6666|777-77-7777|888-88-8888|999-99-9999|123-45-6789|219-09-9999|078-05-1120)(?!666|000|9\d{2})\d{3}[-―](?!00)\d{2}[-―](?!0{4})\d{4}$/
)

Yup.addMethod(Yup.string, 'ssn', function ssn(message: string) {
  return this.test('ssn', message, (ssn) => {
    const rawValue = ssn.replace(/\D/g, '')

    const firstPart = rawValue.substring(0, 3)
    const secondPart = rawValue.substring(3, 5)
    const thirdPart = rawValue.substring(5, 9)

    // regex uses dashes to separate testing of each part of the ssn
    const ssnWithDashes = [firstPart, secondPart, thirdPart]
      .filter(Boolean)
      .join('-')

    return ssnRegex.test(ssnWithDashes)
  })
})

Yup.addMethod(Yup.boolean, 'truthy', function truthy(message: string) {
  return this.test('truthy', message, (value) => value === true)
})

Yup.addMethod(
  Yup.string,
  'differentThan',
  function differentThan(ref: string, message: string) {
    return this.test({
      name: 'differentThan',
      message: message,
      test: function (value) {
        return value !== this.options.context[ref]
      },
    })
  }
)

Yup.addMethod(
  Yup.date,
  'notInTheFuture',
  function notInTheFuture(validationFailureMessage: string) {
    return this.test(
      'notInTheFuture',
      validationFailureMessage,
      (dateToValidate: Date) => dateToValidate < new Date()
    )
  }
)

Yup.addMethod(
  Yup.date,
  'notInThePast',
  function notInThePast(validationFailureMessage: string) {
    return this.test(
      'notInThePast',
      validationFailureMessage,
      (dateToValidate: Date) => {
        // Start @ now, then subtract one day
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)

        return dateToValidate > yesterday
      }
    )
  }
)

Yup.addMethod(
  Yup.date,
  'minAge',
  function minAge(minYears: number, validationFailureMessage: string) {
    if (typeof minYears !== 'number') {
      throw new Error('minYears parameter is required and must be a number')
    }
    return this.test(
      'minAge',
      validationFailureMessage,
      function (birthDate: Date) {
        if (!birthDate) return false

        const today = new Date()
        const age = differenceInYears(today, birthDate)

        return age >= minYears
      }
    )
  }
)

Yup.addMethod(
  Yup.string,
  'fullAddressRequired',
  function fullAddressRequired(ref, message) {
    return this.test({
      name: 'fullAddressRequired',
      message: message,
      test: function () {
        return !!this.options.context[ref]
      },
    })
  }
)

/**
 * Given an array of Questions, outputs Yup Schema for an array of objects.
 * The object keys are Question names and values are the Question's own Yup Schema.
 * */
export function createObjectArraySchema(questions: Questions[]) {
  const objectSchema = questions.reduce(
    (objectSchema, { component, validations, questionName, ...rest }) => {
      if (
        component === 'Question-AddressFieldsAutoComplete' ||
        component === 'Question-AddressAutoComplete' ||
        questionName.name === 'autoCompleteAddressFields'
      ) {
        autoCompleteAddressQuestions.forEach((question) => {
          const { questionName: addressQuestionName, validations } = question
          objectSchema[addressQuestionName] = createYupSchema(validations[0])
        })
        return objectSchema
      }

      if (!validations || validations.length === 0) {
        return objectSchema
      }

      objectSchema[questionName.name] = createYupSchema(validations[0])
      return objectSchema
    },
    {}
  )

  return Yup.array().of(Yup.object(objectSchema))
}

/**
 * Transforms a Question's Validations attribute to Yup Schema.
 * When there is a condition block, rules are only applied when condition evaluates to true.
 * */
export function createYupSchema(validations: Validations) {
  const { validatorType, condition, rules } = validations

  if (!Yup[validatorType]) {
    return {}
  }

  const baseValidator = Yup[validatorType]().nullable()

  if (!rules) return baseValidator

  const validationCondition = condition ? condition[0] : null

  if (!validationCondition) {
    return chainValidationRules(baseValidator, rules)
  }

  return baseValidator.when(validationCondition.reference, {
    // eslint-disable-next-line no-eval
    is: (ref) => eval(validationCondition.is),
    then: chainValidationRules(baseValidator, rules),
  })
}

/**
 * Chains on rules such as `min`, `max`, etc to a base Yup Validator type such as `String` or `Number`.
 * Skips any rules that do not apply to that validator.
 * Ex: `length` rule is not a property of `Number` validator and will be skipped.
 * */
function chainValidationRules(validator, rules: ValidationRule[]) {
  rules.forEach((rule) => {
    if (!validator[rule.name]) {
      return
    }

    let parsedParams

    try {
      parsedParams = JSON.parse(rule.params)
    } catch (error) {
      rollbar.error(
        new CustomError(
          'ChainValidationRulesError',
          'ChainValidationRulesError - failed to parse JSON validation params'
        ),
        { error, rule }
      )
    }

    // Takes regex string from Storyblok, parses to a real RegExp and attaches
    // it back to the validation (NOTE: this modifies by reference)
    parseRegExValidations(parsedParams)

    validator = validator[rule.name](...parsedParams)
  })

  return validator
}

function parseRegExValidations(parsedParams) {
  let regexString: string

  try {
    if (parsedParams?.[0]?.regex) {
      regexString = parsedParams[0].regex
      const parsedRegex = new RegExp(regexString)
      parsedParams[0] = parsedRegex
    }
  } catch (error) {
    rollbar.error(
      new CustomError(
        'ParseRegExValidationsError',
        'ParseRegExValidationsError - failed to parse Storyblok regex string'
      ),
      { error, regexString, parsedParams }
    )
  }
}
